import React, { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { getNextProject, getProjectBySlug, ProjectImage } from '../data/projects';
import { DeepwatchHero } from './DeepwatchHero';
import { DeepwatchCaseStudy } from './DeepwatchCaseStudy';
import { MediaCarousel } from './MediaCarousel';
import { CosmicPrintCaseStudy } from './CosmicPrintCaseStudy';
import { SectionLabel } from './SectionLabel';
import { WinnowCaseStudy } from './WinnowCaseStudy';
export function CaseStudy() {
  const {
    slug = ''
  } = useParams<{
    slug: string;
  }>();
  const project = getProjectBySlug(slug);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, [slug]);
  if (slug === 'deepwatch') {
    return <DeepwatchCaseStudy data-id="element-69" data-mp="77izeqo83jw8" />;
  }
  if (slug === 'winnow') {
    return <WinnowCaseStudy data-id="element-70" data-mp="75y24taixmuo" />;
  }
  if (slug === 'cosmic-print') {
    return <CosmicPrintCaseStudy data-id="element-71" data-mp="2lrgh9hr5yv90" />;
  }
  if (!project) {
    return <MissingProject data-id="element-72" data-mp="2grtzqw4aysue" />;
  }
  const next = getNextProject(project.slug);
  const titleWords = project.title.split(' ');
  const accentWord = titleWords.pop() ?? project.title;
  const titleStart = titleWords.join(' ');
  return <article className="pb-24 md:pb-32" data-id="element-73" data-mp="27rxhbc8iaq7s">
      <div className="site-container pt-28 md:pt-36" data-id="element-74" data-mp="27syxfv4dq2wd">
        <Link to="/#work" className="group inline-flex items-center gap-2 font-display text-base font-medium uppercase tracking-[0.1em] text-muted transition-colors duration-200 hover:text-cobalt focus-visible:outline-none focus-visible:text-cobalt" data-id="element-75" data-mp="34g3ghjmnsqeo">
          <ArrowLeftIcon size={16} aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-76" data-mp="2qq9v5bad4o9p" />
          Back to work
        </Link>
      </div>

      <header className="site-container pb-14 pt-12 md:pb-16 md:pt-16" data-id="element-77" data-mp="3ih64i1x7sas8">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-78" data-mp="3ieti9ckfbe9d">
          <SectionLabel data-id="element-79" data-mp="1lkb98e9j2ukj">{project.category}</SectionLabel>
        </motion.div>
        <motion.h1 initial={reduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        delay: reduceMotion ? 0 : 0.04,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-7 max-w-4xl text-balance text-4xl font-medium leading-[1.04] tracking-[-0.04em] md:text-6xl lg:text-7xl" data-id="element-80" data-mp="16tarfimw5obq">
          {titleStart && <>{titleStart} </>}
          <span className="font-normal italic" data-id="element-81" data-mp="nwt4rfmwgemc">{accentWord}</span>
        </motion.h1>
      </header>

      <motion.div initial={reduceMotion ? false : {
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.12,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container" data-id="element-82" data-mp="svvyi3xfiyf0">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-border md:aspect-[2/1]" data-id="element-83" data-mp="bliiby9eil67">
          {project.slug === 'deepwatch' ? <DeepwatchHero variant="case-study" data-id="element-84" data-mp="6jb38zt9yozn" /> : <img src={project.heroImage} alt={`${project.title} — project overview`} className="h-full w-full object-cover" loading="eager" decoding="async" data-id="element-85" data-mp="lajxjm84gyj4" />}
        </div>
      </motion.div>

      <motion.dl initial={reduceMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.16,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-8 grid grid-cols-2 gap-x-6 gap-y-7 md:mt-10 md:grid-cols-4 md:gap-x-10" data-id="element-86" data-mp="q9wlyben6dnt">
        <Meta label="Client" value={project.client} data-id="element-87" data-mp="361aqkcl51mxr" />
        <Meta label="Role" value={project.role} data-id="element-88" data-mp="311obdxlr1wmb" />
        <Meta label="Year" value={project.year} data-id="element-89" data-mp="312zf5xf734df" />
        <Meta label="Duration" value={project.duration} data-id="element-90" data-mp="32iu0k5f7x9ip" />
      </motion.dl>

      <CaseSection label="Overview" title="The product" data-id="element-91" data-mp="32ky3ak1ds2he"><p data-id="element-92" data-mp="37gn6cxcm01ig">{project.overview}</p></CaseSection>
      <CaseSection label="Challenge" title="The challenge" data-id="element-93" data-mp="37jrhkeohrxim"><p data-id="element-94" data-mp="3cht110ptxyum">{project.challenge}</p></CaseSection>
      <CaseSection label="Approach" title="The approach" data-id="element-95" data-mp="3ceon3bmkboqr"><p data-id="element-96" data-mp="3hg47r2ktvt8m">{project.approach}</p></CaseSection>

      {project.slug === 'deepwatch' && <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch design system" data-id="element-97" data-mp="2jsm0jw6tez6q">
          <MediaCarousel label="Design system" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/5NtYA6VDjuKpKMJoctKbbx/ds-atoms.png',
        alt: 'Deepwatch design-system form elements and foundational controls',
        caption: 'Foundations — form elements and control states'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/tfFe9QhPwLKdDfJsgdUuPF/ds-dataviz.png',
        alt: 'Deepwatch data visualization components and dashboard chart patterns',
        caption: 'Data visualization — dashboard and chart patterns'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/kVMmyLYyE3GpQyxojhAc6G/ds-organisms.png',
        alt: 'Deepwatch design-system organisms including messages, notifications, and date pickers',
        caption: 'Scaled patterns — messaging, notifications, and date pickers'
      }]} data-id="element-98" data-mp="2oqdx53ien9h9" />
        </section>}

      {project.slug === 'deepwatch' && <section className="site-container my-20 max-w-6xl md:my-28" aria-label="Deepwatch research" data-id="element-99" data-mp="3rkuf5nz03pk8">
          <MediaCarousel label="Research and synthesis" items={[{
        src: 'https://cdn.magicpatterns.com/uploads/a2hSvUEVihWWgjtphC9uQd/dw-research.png',
        alt: 'Deepwatch research board with interview notes from customers and internal teams',
        caption: 'Interview capture — customer and internal feedback'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/25MPN6ixEdBHYmr4EGkrTY/dw-research-clustering.png',
        alt: 'Deepwatch research board clustering feedback into environment health and data visualization themes',
        caption: 'Clustering — organizing themes and opportunity areas'
      }, {
        src: 'https://cdn.magicpatterns.com/uploads/vwd59pKMyRawcycp46vv8p/dw-research-synthesis.png',
        alt: 'Deepwatch Environment Health research synthesis with interview insights and open questions',
        caption: 'Synthesis — Environment Health findings and open questions'
      }]} data-id="element-100" data-mp="3mlre0em563hj" />
        </section>}

      {project.gallery.length > 0 && <div className="site-container my-24 space-y-8 md:my-32" data-id="element-101" data-mp="damcgxgm0mi9">
          {project.gallery.map((image, index) => {
        const imageData = toProjectImage(image, project.title, index);
        return <motion.figure key={`${imageData.src}-${index}`} initial={reduceMotion ? false : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.28,
          delay: index * 0.04,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-102" data-mp="1t0difu7bxjfo" data-mpi={index}>
                <div className={`overflow-hidden bg-border ${index % 2 === 0 ? 'aspect-video' : 'aspect-[4/3] md:aspect-[16/10]'}`} data-id="element-103" data-mp="1a2u88n94xuro">
                  <img src={imageData.src} alt={imageData.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" data-id="element-104" data-mp="1p0ml17nas526" />
                </div>
                {imageData.caption && <figcaption className="mt-3 font-display text-xs font-medium uppercase tracking-[0.08em] text-muted" data-id="element-105" data-mp="2vhhwgc4trn5g">
                    {imageData.caption}
                  </figcaption>}
              </motion.figure>;
      })}
        </div>}

      <CaseSection label="Outcome" title="The outcome" data-id="element-106" data-mp="12pl77xgjccq6"><p data-id="element-107" data-mp="17i4ksuf837uz">{project.outcome}</p></CaseSection>

      <div className="site-container mt-24 md:mt-32" data-id="element-108" data-mp="17j79qwszj089">
        <SectionLabel data-id="element-109" data-mp="1ch8s1fil6vio">Next project</SectionLabel>
        <Link to={`/work/${next.slug}`} className="group mt-10 flex items-end justify-between gap-6 border-b border-border pb-8 focus-visible:outline-none" data-id="element-110" data-mp="1cgfvf7dcdme9">
          <div className="min-w-0" data-id="element-111" data-mp="1lhx7menmp3xo">
            <h2 className="break-words text-4xl font-medium leading-[1.03] tracking-[-0.035em] transition-colors duration-200 group-hover:text-cobalt md:text-6xl" data-id="element-112" data-mp="1lfkpa0dykrk3">
              {next.title}
            </h2>
            <p className="mt-3 text-muted" data-id="element-113" data-mp="3s7u0f9nr2xoq">{next.client}</p>
          </div>
          <ArrowUpRightIcon size={34} aria-hidden="true" className="mb-1 shrink-0 transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cobalt" data-id="element-114" data-mp="3s9y0fgij3cjq" />
        </Link>
      </div>
    </article>;
}
function MissingProject() {
  return <div className="site-container flex min-h-screen flex-col items-center justify-center text-center" data-id="element-115" data-mp="3aysskb40xlpx">
      <SectionLabel data-id="element-116" data-mp="3619at184aqt8">404</SectionLabel>
      <h1 className="mt-8 text-4xl font-medium tracking-[-0.035em] md:text-5xl" data-id="element-117" data-mp="7hj145jl225g">That project doesn’t exist.</h1>
      <Link to="/" className="mt-8 inline-flex items-center gap-2 font-display text-base font-medium uppercase tracking-[0.1em] text-foreground transition-colors duration-200 hover:text-cobalt focus-visible:outline-none focus-visible:text-cobalt" data-id="element-118" data-mp="2ig3h65xmy7i">
        <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-119" data-mp="3os0js1o70f73" /> Back to home
      </Link>
    </div>;
}
function toProjectImage(image: string | ProjectImage, projectTitle: string, index: number): ProjectImage {
  if (typeof image !== 'string') return image;
  return {
    src: image,
    alt: `${projectTitle} — image ${index + 1}`
  };
}
function Meta({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-120" data-mp="33b9utjxy1pzt">
      <dt className="font-display text-xs font-medium uppercase tracking-[0.1em] text-muted" data-id="element-121" data-mp="3i2ismgc02ho1">{label}</dt>
      <dd className="mt-1.5 break-words text-sm leading-snug text-foreground md:text-base" data-id="element-122" data-mp="3dasfy3plbhtg">{value}</dd>
    </div>;
}
function CaseSection({
  label,
  title,
  children
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  return <motion.section initial={reduceMotion ? false : {
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.28,
    ease: [0.22, 1, 0.36, 1]
  }} className="site-container my-20 md:my-28" data-id="element-123" data-mp="4v8ona8hc9h1">
      <div className="max-w-3xl" data-id="element-124" data-mp="21nqzg17156do">
        <SectionLabel data-id="element-125" data-mp="21osm72xerhb8">{label}</SectionLabel>
        <h2 className="mt-5 text-3xl font-medium leading-[1.08] tracking-[-0.03em] md:text-5xl" data-id="element-126" data-mp="21pvdvd2k1ns7">{title}</h2>
        <div className="mt-6 max-w-2xl text-base leading-relaxed text-muted md:text-lg" data-id="element-127" data-mp="h82nu88fdvu7">{children}</div>
      </div>
    </motion.section>;
}